import React from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';

export const inputBaseSx = (palette, theme, mono) => ({
  width: '100%',
  fontFamily: mono ? 'monospace' : 'inherit',
  fontSize: NODE_UI.fontSize.value,
  color: palette.textPrimary,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${palette.border}`,
  borderRadius: NODE_UI.borderRadiusSm,
  padding: NODE_UI.input.padding,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': { borderColor: palette.textSecondary },
  '&.Mui-focused': {
    borderColor: palette.textPrimary,
    boxShadow: `0 0 0 1px ${palette.textPrimary}`,
  },
  '& input::placeholder, & textarea::placeholder': { color: palette.textSecondary, opacity: 0.6 },
});

export function SettingsInput({ label, value, onChange, placeholder, mono, palette, theme, type = 'text' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography sx={{ fontSize: NODE_UI.fontSize.caption, fontWeight: NODE_UI.fontWeight.label, color: palette.textSecondary, userSelect: 'none' }}>
        {label}
      </Typography>
      <InputBase
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
        sx={inputBaseSx(palette, theme, mono)}
      />
    </Box>
  );
}

export function SettingsRow({ children }) {
  return (
    <Box sx={{ display: 'flex', gap: '6px', '& > *': { flex: 1 } }}>
      {children}
    </Box>
  );
}

export function SettingsToggle({ label, checked, onChange, palette }) {
  return (
    <Box
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: '2px',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Typography sx={{ fontSize: NODE_UI.fontSize.value, color: palette.textPrimary }}>{label}</Typography>
      <Box
        sx={{
          width: 28,
          height: 16,
          borderRadius: '100px',
          backgroundColor: checked ? palette.textPrimary : 'transparent',
          border: `1.5px solid ${checked ? palette.textPrimary : palette.textSecondary}`,
          boxSizing: 'border-box',
          position: 'relative',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: checked ? palette.surface : palette.textSecondary,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: checked ? 14 : 2,
            transition: 'left 0.15s ease, background-color 0.15s ease',
          }}
        />
      </Box>
    </Box>
  );
}

export function SectionHeader({ label, palette }) {
  return (
    <Typography sx={{
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: palette.textSecondary,
      mt: '4px',
      userSelect: 'none',
    }}>
      {label}
    </Typography>
  );
}

export function SegmentedPicker({ label, value, options, onChange, palette }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography sx={{ fontSize: NODE_UI.fontSize.caption, fontWeight: NODE_UI.fontWeight.label, color: palette.textSecondary, userSelect: 'none' }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: '1px',
          p: '2px',
          backgroundColor: palette.border,
          borderRadius: NODE_UI.borderRadiusSm,
        }}
      >
        {options.map((opt) => (
          <Box
            key={opt.value}
            onClick={(e) => { e.stopPropagation(); onChange(opt.value); }}
            sx={{
              flex: 1,
              textAlign: 'center',
              py: '3px',
              px: '6px',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: NODE_UI.fontSize.caption,
              fontWeight: value === opt.value ? 600 : 400,
              color: value === opt.value ? palette.textPrimary : palette.textSecondary,
              backgroundColor: value === opt.value ? palette.surface : 'transparent',
              transition: 'all 0.1s ease',
              userSelect: 'none',
            }}
          >
            {opt.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
